import {
    AvailabilityContract,
    ComboChoiceContract,
    ComboChoiceItemModifierOptionContract,
    ComboChoiceType,
    CountryContract,
    DeliveryDetailsContract,
    DiscountContract,
    ItemCategoryLiteContract,
    ItemCategoryModifierContract,
    ItemModifierContract,
    ItemType,
    KitchenPrintStatus,
    LinkedModifierType,
    LocationOrderChannelContract,
    MenuItemContract,
    MenuItemModifierOptionContract,
    ModifierGroupContract,
    ModifierOptionContract,
    NotificationChannelContract,
    OnlineStoreContract,
    OrderChannelContract,
    OrderDetailContract,
    OrderInventoryStatus,
    OrderLineKdsStatus,
    OrderLineStatus,
    POSCombinedModel,
    POSPriceListContract,
    POSProductContract,
    PosTerminalMenuSlimContract,
    POSVariantGroup,
    POSVariantOption,
    ProductContract,
    ReceiptTemplateMarkupSlimModel,
    SaleContract,
    ServiceChargeSettingDetailContract,
    TaxContract,
    UnavailableItemsContract,
    VariantOptionModifierContract,
    VoidCompReasonModel,
} from 'models'
import { Dictionary, PickRequired } from 'utilities/typeUtility/typeUtility'
import { OrderChannel } from 'constant/OrderChannels'
import dayjs, { Dayjs } from 'dayjs'
import { TOldSaleData } from 'views/Sale/hooks/useSaleSelectReducer/useSaleSelectReducer'
import { BaseOrderItem } from 'views/Sale/utils/orderCalculation/common'
import { ICalculationResultModel } from 'views/Sale/utils/orderCalculation/models/IOrderCalculationModel'
export interface IServiceCharge {
    setting?: ServiceChargeSettingDetailContract
    taxes?: TaxContract[]
    isTaxInclusive: boolean
    orderServiceChageRate: number
}
export interface POSModifierOption {
    modifierOptionID?: string
    name?: string | null
    isAvailable?: boolean
    modifierGroupID?: string
    sortNumber?: number
}
export interface POSModifierGroup {
    modifierGroupID?: string
    name?: string | null
    sortNumber?: number
    createdOn?: string
    modifierOptions?: Array<POSModifierOption> | null
}
export interface KitchenPrintStatusUpdate {
    orderLineIds: string[]
    newStatus: KitchenPrintStatus
}

export interface IOrderServiceChargeSetting {
    disableServiceCharge: boolean
    useManualServiceCharge: boolean
    currentServiceChageRate: number
}

export interface ISellDependency {
    categories: ItemCategoryLiteContract[]
    posData: POSCombinedModel
    variantGroups: POSVariantGroup[]
    orderChannels: OrderChannelContract[]
    terminalMenu: PosTerminalMenuSlimContract
    country: CountryContract
    defaultTemplate: ReceiptTemplateMarkupSlimModel[]
    notificationChannels: NotificationChannelContract[]
    unavailableProducts: UnavailableItemsContract
    enableInventoryTracking: boolean
    locationOrderChannels: LocationOrderChannelContract[]
}

export interface IModifyItem {
    item: OrderItemViewModel
    newItem: OrderItemViewModel
}

export enum SellProcessingState {
    Idle,
    Processing,
    Success,
    Failed,
    Voided,
}
export interface IMenuItemDetail extends Pick<
    MenuItemContract,
    'isAvailable' | 'menuID' | 'menuItemID' | 'itemID'
> {
    baseItemID: string | undefined
    menuSectionID: string | undefined
    availabilities: AvailabilityContract[]
    menuItemModifierOptions: Pick<
        MenuItemModifierOptionContract,
        'isAvailable' | 'modifierOptionID'
    >[]
}

export interface MenuViewModel {
    menuId: string
    name: string
    categories: CategoryViewModel[]
    categoryMap: { [cateID: string]: CategoryViewModel }
    defaultBackgroundColor?: string | null
    isUserDefinedMenu: boolean
    brandID: string
    items: ItemViewModel[]
    isOnlineMenu: boolean
    availabilities: AvailabilityContract[]
    menuSectionAvailabilitiesMap: {
        [menuSectionId: string]: AvailabilityContract[]
    }
    menuItemCategoryAvailabilitiesMap: {
        [categoryId: string]: AvailabilityContract[]
    }
    menuItemDetailMap: { [menuItemId: string]: IMenuItemDetail }
    priceListID: string | undefined
    menuSections: MenuSectionViewModel[]
}
export interface MenuSectionViewModel {
    menuSectionId: string
    name: string
    subMenuSections: MenuSectionViewModel[]
    parentMenuSectionId?: string
    displayOrder: number
}
export interface CategoryViewModel {
    categoryId: string
    name: string
    sortNumber: number
}

export interface ItemViewModel {
    itemId: string
    /**
     * comboChoiceItemId use for get price list item in comboItemPanel
     */
    comboChoiceItemId?: string
    categoryId: string
    name: string
    displayName: string
    barcode?: string
    description?: string | null
    baseItemId?: string
    baseDepositAndCreditItemId?: string
    /**
     * @deprecated
     * item base price and will change to real price when add to order item view model
     */
    price?: number
    hasAdditionalPrice?: boolean
    isTaxInclusive: boolean
    tax?: TaxContract
    variantGroups: POSVariantGroupWithPrice[]
    variations: ItemViewModel[]
    depositAndCreditItems: ItemViewModel[]
    combinationIds: string[]
    imageUrl: string
    type: ItemType
    isAvailableForSelection: boolean
    brandID: string
    enableTrackInventory: boolean
    isActivateInLocation?: boolean
    /** list of menu section id that container the item map with order in the section */
    inMenuSections: Record<string, number | undefined>
    isOutOfStock: boolean
    overrideCategoryModifierSettings: OverrideCategoryModifierSettings[]
}

export interface ModifierViewModel {
    modifierOptionId: string
    displayName: string
    isRequired: boolean
    priceAdd: number
}

export interface EntityModifierViewModel {
    /** Id of ItemModifier, ItemCategoryModifier, VariantOptionModifier */
    entityModifierId: string

    /** Id of Item, ItemCategory, VariantOption */
    entityId: string
    entityType: LinkedModifierType
    displayName: string
    isRequired?: boolean
    isActive: boolean
    maxSelectedOptions: number
    minSelectedOptions: number
    modifierGroupId: string
    hasAssociatedCost: boolean
    sortNumber: number
}

export interface UsedModifierOption {
    option: ModifierOptionContract
    metadata: EntityModifierViewModel
}

export interface UsedItem {
    /** selected product or base product ID */
    nodeItemId: string
    quantity: number

    /** Combo Node ID */
    nodeId: string

    comboChoiceId: string
    comboItemPath: string

    /** flag if it item from combo choice type fixedProduct*/
    isFixedItem: boolean
    usedVariants: UsedVariants
    usedModifiers: UsedModifiers
    addPrice: number
}

export interface DiscountContractWithCustom extends DiscountContract {
    isCustomDiscount?: boolean
    discountRedemptionId?: string
    orderLineDiscountId?: string
}
export interface DiscountContractWithItemId
    extends DiscountContract, ICartItem {
    /** orderItem id */
    id: string | undefined
    orderLineDiscountId?: string
}

export interface VoidCompReasonModelWithItemId
    extends VoidCompReasonModel, ICartItem {
    /** orderItem id */
    id: string | undefined
}

export enum SelectItemInCartType {
    item = 'item',
    discount = 'discount',
    serviceCharge = 'serviceCharge',
    Comp = 'Comp',
}
interface ICartItem {
    /** orderItem id
     * null = order level
     */
    id: string | undefined
}
export type ISelectItemInCart =
    | {
          type: SelectItemInCartType.item
          data: OrderItemViewModel
      }
    | {
          type: SelectItemInCartType.discount
          data: DiscountContractWithItemId
      }
    | {
          type: SelectItemInCartType.serviceCharge
          data?: any //no data,because only order level
      }
    | {
          type: SelectItemInCartType.Comp
          data: VoidCompReasonModelWithItemId
      }
export interface ModifierOptionData {
    modifierGroupId: string
    modifierOptionId: string
    addPrice: number
}
export interface UsedModifier extends ModifierOptionData {
    subModifierOptions: UsedSubModifier
}

export type ModifierOptionLookup = {
    [modifierOptionId: string]: ModifierOptionContract
}

export type EntityModifiersMap = {
    [entityModifierID: string]: EntityModifierViewModel
}
export type ItemMap = Record<string, ItemViewModel>
export type UsedVariants = { [variantGroupId: string]: string }
export type UsedModifiers = { [bindingModifierId: string]: UsedModifier[] }
export type UsedSubModifier = {
    [bindingModifierId: string]: ModifierOptionData[]
}

/** pathToComboItem = `<rootCombo_Id(ComboChoiceID)>,<comboItem_Id> */
export type UsedItems = { [pathToComboItem: string]: UsedItem }
export interface usedModWithQuantity extends UsedModifier {
    quantity: number
}
export type ErrorOrderItemViewType = {
    [errorType in
        | 'item-unavailable'
        | 'price-changed'
        | 'time-unavailable'
        | 'invalid-modifier-option']: {
        message: string
        isHiddenShowError?: boolean
    }[]
}
export interface OrderItemViewModel extends BaseOrderItem, ICartItem {
    /**orderLineID or temp id if not save */
    id: string
    item: ItemViewModel
    note: string
    orderID?: string

    /** overridden display name of current line. Currently used in Open Item */
    displayName?: string

    /** overridden tax ID. Currently used in Open Item */
    overriddentaxID?: string | null
    /** overridden Is Tax Inclusive flag. Currently used in Open Item */
    overriddenIsTaxInclusive?: boolean

    //order item form
    /** map between <variantGroupId, selectedVariantOptionId> used for root product item. For combo item (subItem), use usedItem.usedVariants */
    usedVariants: UsedVariants

    /** map between <bindingModifierId, UsedModifier[]> used for root product item. For combo item (subItem), use usedItem.usedModifiers */
    usedModifiers: UsedModifiers
    /** pathToComboItem = `<rootCombo_Id(ComboChoiceID)>,<comboItem_Id> */
    usedItems: UsedItems

    error?: ErrorOrderItemViewType
    isSaved: boolean

    menuId?: string
    menuSectionId: string
    isItemUnavailable?: boolean

    createdOn: Dayjs
    status: OrderLineStatus
    kdsStatus: OrderLineKdsStatus
    kitchenPrintStatus: KitchenPrintStatus
    inventoryStatus: OrderInventoryStatus

    /* table services */
    /** ids of bill (order) that current line assigned to */
    assignTo?: string[]
    /** seat note that current line assigned to. In format (table name)@(seat no),... (can repeat using comma as delimitor)  */
    seatNote?: string | null

    /** for calculate split line real time in CFD */
    subSharedLines?: OrderItemViewModel[]
    isSharedOrderLine?: boolean
    isSubSharedOrderLine?: boolean
    hasOrderLinePaid?: boolean
    sharedOrderLineID?: string
    isAutoAppliedDiscount?: boolean
}
export interface POSVariantGroupWithPrice extends Omit<
    POSVariantGroup,
    'variantOptions'
> {
    variantOptions: POSVariantOptionWithPrice[]
}
export interface POSVariantOptionWithPrice extends POSVariantOption {
    addPrice: number
    isDefaultOption: boolean
}

export interface ComboModifierGroup extends Omit<
    POSModifierGroup,
    'modifierOptions'
> {
    modifierOptions: ComboModifierSetting[]
}
export interface ComboModifierSetting extends POSModifierOption {
    entityModifierId: string
    addPrice: number
    isDefaultOption: boolean
    combinationIndices?: string
}

export enum OrderProcessingMode {
    NON_SELECTED,
    SAVE,
    FINISH,
    PREVIEW,
}

export enum OrderFulfilmentType {
    DINE_IN = 1,
    TAKEAWAY = 2,
    PICKUP = 3,
    DELIVERY = 4,
}
export interface IOnlineStoreWithBrand extends OnlineStoreContract {
    brandID?: string
}

interface GlobalPOSState {
    orderChannelID: OrderChannel
    //Cart UI
    /** Old data for display loaded old order*/
    removedData: TOldSaleData
}
export interface IPreviousSelected {
    categoryId: string
    menuSectionId: string
}
export interface IProductPanelState {
    /** Items that show in product panel list */
    items: ItemViewModel[]
    selectedMenu?: MenuViewModel
    selectOnlineStoreId?: string
    selectedCategoryId: string
    selectedMenuSectionId: string
    searchString: string
    selectedBrandID?: string
    /** POS-7474 **use for category filtered displayed only** :
     * Save selecetd category state to maintain the category list before search
     * in case the search result is not contain in the currect selected category/menuSection
     * to make selected category/menuSection show even no result in category/menuSection and change to other when searching still show too
     * */
    previousSelected: IPreviousSelected

    //Cart UI
    removedData: TOldSaleData
}
export interface SellState extends IProductPanelState, GlobalPOSState {
    //QS only
    isCheckingOrderStockAvailability: boolean
    isCustomerDialogOpen: boolean
    isReceiptDialogOpen: boolean
    isVoidDialogOpen: boolean
    isGiftCardDialogOpen: boolean
    orderSale?: SaleContract | undefined
    orderNote: string
    taxResult: ICalculationResultModel
    sellProcessingState: SellProcessingState
    sellProcessingMessage: string
    sellProcessingAdditionValue?:
        | { name: string; orderQuantity: number; stock: number }
        | undefined
    orderProcessingMode: OrderProcessingMode
    entireOrderDiscounts: DiscountContractWithCustom[]
    entireOrderVoidComp?: VoidCompReasonModel
    selectCustomerAddressId?: string
    orderCustomerID?: string
    editingOrder?: OrderDetailContract | undefined
    savedOrderItems: OrderItemViewModel[]

    // Current order service charge setting
    orderServiceChargeSetting: IOrderServiceChargeSetting
    defaultServiceChargeRate: number

    //cart UI
    latestOrderItem?: OrderItemViewModel
    orderItems: OrderItemViewModel[]
    selectedItemInCart?: ISelectItemInCart

    //Discount popup
    //Exclude to process auto after removed
    excludeAutoDiscountIds: string[]
    openAlertDiscountInApplicable: boolean

    //only feature that have in QS
    //bill review
    billPreviewOrderID?: string
    /** for pickup fulfillment */
    pickupDateTime?: string | null
    isAsap?: boolean | null
    deliveryAddress: DeliveryDetailsContract
    fulfilmentLoading: boolean
    activeFulfillment: number[]
    currentFulfilmentType: OrderFulfilmentType

    loadOrderState: SellProcessingState
    hasAwaitingProcessTransaction: boolean

    /**
     * Id of Order Lines that waiting for response from KDS
     * The purpose of this state variable is in specific case, e.g. to block saving an order that the order line are in sending state.
     **/
    waitingSentToKdsOrderLineIds: string[]
}
export interface PriceListData {
    basePriceList: POSPriceListContract
    menuPriceListMap: { [menuID: string]: POSPriceListContract }
}

export interface ComboViewModel {
    comboChoiceID: string
    name: string
    itemId: string
    quantity: number
    type: ComboChoiceType
    createdOn: string
    comboItems: ComboItemViewmodel[]
    sortNumber?: number
}
export interface POSVariantGroupWithPrice extends POSVariantGroup {
    variantOptions: POSVariantOptionWithPrice[]
}
export interface OverrideCategoryModifierSettings {
    itemCategoryModifierID: string
    isActive: boolean
}
export interface ComboModifierGroup extends POSModifierGroup {
    modifierOptions: ComboModifierSetting[]
}
export interface ComboItemViewmodel {
    comboChoiceItemId: string
    itemID: string
    addPrice: number
    item: ItemViewModel
    variantGroups: POSVariantGroupWithPrice[]
    comboChoiceID: string
}
export interface ComboItemModifierOptionViewModel extends PickRequired<
    ComboChoiceItemModifierOptionContract,
    | 'combinationIndices'
    | 'modifierOptionID'
    | 'isDefaultOption'
    | 'comboChoiceItemID'
    | 'isActive'
> {
    /** itemModifierID, variantOptionModifierID, modifierOptionModifierID, ItemCategoryModifierID*/
    bindingModifierId: string
    priceAdd: number
}
export interface IPickupSettingForm {
    pickupDate: dayjs.Dayjs
    pickupTime: dayjs.Dayjs
    customerId: string | undefined
    isAsap: boolean
}

export interface IDeliverySettingForm extends IPickupSettingForm {
    deliveryAddress: DeliveryDetailsContract
}

export function convertToComboViewModel(
    comboChoice: ComboChoiceContract,
): ComboViewModel {
    return {
        comboChoiceID: comboChoice.comboChoiceID || '',
        itemId: comboChoice.itemID || '',
        quantity: comboChoice.quantity || 0,
        name: comboChoice.displayName ?? '',
        type: comboChoice.type ?? ComboChoiceType.FixedProduct,
        createdOn: comboChoice.createdOn ?? '',
        comboItems: [],
        sortNumber: comboChoice.sortNumber,
    }
}

export function createEntityModifierViewModel(
    i:
        | ItemModifierContract
        | ItemCategoryModifierContract
        | VariantOptionModifierContract,
    id: string,
    entityId: string,
    type: LinkedModifierType,
    modifierGroupsMap: Dictionary<ModifierGroupContract>,
): EntityModifierViewModel {
    return {
        entityModifierId: id,
        entityId: entityId,
        entityType: type,
        displayName:
            i.displayName ||
            modifierGroupsMap[i.modifierGroupID!!].name ||
            'Modifier',
        maxSelectedOptions: i.maxSelectedOptions || 0,
        minSelectedOptions: i.minSelectedOptions || 0,
        modifierGroupId: i.modifierGroupID!!,
        isRequired: !!i.isRequired,
        isActive: !!i.isActive,
        hasAssociatedCost: i.hasAssociatedCost ?? true,
        sortNumber:
            'sortNumber' in i ? i.sortNumber!! : Number.MAX_SAFE_INTEGER,
    }
}

export function convertToItemViewModel(i: POSProductContract): ItemViewModel {
    const overrideCategoryModifierSettings =
        i.itemItemCategoryModifierSettings?.map<OverrideCategoryModifierSettings>(
            (a) => ({
                itemCategoryModifierID: a.itemCategoryModifierID ?? '',
                isActive: a.isActive ?? false,
            }),
        ) ?? []
    const variantItems =
        i.itemVariations?.map((v: ProductContract) => ({
            ...convertToItemViewModel(v),
            baseItemId: i.itemID,
            type: ItemType.ProductVariant,
            overrideCategoryModifierSettings: overrideCategoryModifierSettings,
        })) || []
    const variantGroups =
        i.variantGroups?.map<POSVariantGroupWithPrice>((g) => {
            const newType: POSVariantGroupWithPrice = {
                ...g,
                variantOptions:
                    g.variantOptions?.map<POSVariantOptionWithPrice>((o) => ({
                        ...o,
                        addPrice: 0,
                        isDefaultOption: false,
                    })) ?? [],
            }
            return newType
        }) ?? []
    const depositItems =
        i.type === ItemType.BaseDepositAndCreditProduct
            ? (i.depositAndCreditItemVariations?.map((dci) =>
                  convertToItemViewModel(dci),
              ) ?? [])
            : []

    return {
        itemId: i.itemID!!,
        categoryId: i.categoryID || '',
        name: i.name || '',
        displayName: i.displayName || i.name || '',
        barcode: i.barcode || '',
        description: i.description,
        price: 0,
        hasAdditionalPrice: false,
        variantGroups: variantGroups,
        isTaxInclusive: i.isTaxInclusive || false,
        tax: i.tax,
        type: i.type ?? ItemType.Product,
        imageUrl: i.imageUrl || '',
        isAvailableForSelection:
            // Item should have these 2 flags been TRUE for enable to be sold
            (i.isAvailableForSelection || false) && (i.isActive || false),
        combinationIds: i.combinationIndices?.split('*') || [],
        brandID: i.brandID || '',
        variations: variantItems,
        depositAndCreditItems: depositItems,
        enableTrackInventory: i.enableTrackInventory || false,
        isActivateInLocation: i.hasLocationData,
        inMenuSections: {},
        isOutOfStock: false,
        baseItemId: i.baseItemID ?? undefined,
        baseDepositAndCreditItemId: i.baseDepositAndCreditItemID ?? undefined,
        overrideCategoryModifierSettings: overrideCategoryModifierSettings,
    }
}

export const emptyDeliveryAddress: () => DeliveryDetailsContract = () => ({
    customerAddressID: null,
    addressName: '',
    fullAddress: '',
    deliveryNote: '',
})
